/**
 * openai-stream-tools.ts
 * OpenAI 兼容协议流式 tool_call 增量累积——纯函数，无副作用、无外部依赖。
 * 供兼容路径手动工具循环（llm.ts）使用，替代 xsAI streamText 黑盒的内部累积。
 *
 * 防御式设计（应对第三方网关差异）：
 * - id/name 取「首个非空」而非「仅首帧」，容忍网关延迟给出 id/name；
 * - arguments 恒累加（无论增量还是整段）；
 * - 按 delta.tool_calls[].index 聚合，容忍乱序/非连续 index；
 * - finish_reason 两种拼写（tool_calls / tool-calls）都识别为「需续轮」；
 * - 畸形 chunk 静默跳过，绝不抛错中断流。
 */

export interface AccumulatingToolCall {
  index: number
  id: string
  name: string
  arguments: string
}

export interface OpenAIStreamState {
  textContent: string
  reasoningContent: string
  toolCalls: Map<number, AccumulatingToolCall>
  finishReason: string
}

export interface ParsedToolCall {
  id: string
  name: string
  input: unknown
  inputError?: string
  rawArguments?: string
}

export const INVALID_TOOL_INPUT_MESSAGE = '工具参数不是有效的 JSON。工具未执行，请修正参数后重试。'

export function parseToolArguments(rawArguments: string): { input: unknown, error?: string } {
  try {
    return { input: JSON.parse(rawArguments) }
  }
  catch {
    // 错误参数不能当成空对象执行，否则可能误触发带默认值的动作。
    return { input: undefined, error: INVALID_TOOL_INPUT_MESSAGE }
  }
}

export function createOpenAIStreamState(): OpenAIStreamState {
  return { textContent: '', reasoningContent: '', toolCalls: new Map(), finishReason: '' }
}

export function isToolCallsFinish(finishReason: string): boolean {
  return finishReason === 'tool_calls' || finishReason === 'tool-calls'
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return typeof v === 'object' && v !== null ? v as Record<string, unknown> : undefined
}

export function reduceOpenAIChunk(state: OpenAIStreamState, chunk: unknown): void {
  const obj = asRecord(chunk)
  if (!obj)
    return
  const choices = obj.choices
  if (!Array.isArray(choices) || choices.length === 0)
    return
  const choice = asRecord(choices[0])
  if (!choice)
    return

  if (typeof choice.finish_reason === 'string' && choice.finish_reason)
    state.finishReason = choice.finish_reason

  const delta = asRecord(choice.delta)
  if (!delta)
    return

  if (typeof delta.content === 'string')
    state.textContent += delta.content
  if (typeof delta.reasoning_content === 'string')
    state.reasoningContent += delta.reasoning_content

  const toolCalls = delta.tool_calls
  if (!Array.isArray(toolCalls))
    return
  for (const rawTc of toolCalls) {
    const tc = asRecord(rawTc)
    if (!tc || typeof tc.index !== 'number')
      continue
    const fn = asRecord(tc.function)
    let entry = state.toolCalls.get(tc.index)
    if (!entry) {
      entry = { index: tc.index, id: '', name: '', arguments: '' }
      state.toolCalls.set(tc.index, entry)
    }
    // id/name 取首个非空，不被后续空值覆盖
    if (typeof tc.id === 'string' && tc.id && !entry.id)
      entry.id = tc.id
    if (fn && typeof fn.name === 'string' && fn.name && !entry.name)
      entry.name = fn.name
    if (fn && typeof fn.arguments === 'string')
      entry.arguments += fn.arguments
  }
}

export function finalizeToolCalls(state: OpenAIStreamState): ParsedToolCall[] {
  return [...state.toolCalls.values()]
    .sort((a, b) => a.index - b.index)
    .filter(tc => tc.id && tc.name)
    .map((tc) => {
      const parsed = parseToolArguments(tc.arguments)
      return {
        id: tc.id,
        name: tc.name,
        input: parsed.input,
        inputError: parsed.error,
        rawArguments: parsed.error ? tc.arguments : undefined,
      }
    })
}
